export enum DocumentType {
  DECLARATION_COVENANTS = "declaration_covenants",
  BYLAWS = "bylaws",
  RULES_REGULATIONS = "rules_regulations",
  ARCHITECTURAL_GUIDELINES = "architectural_guidelines",
  ELECTION_RULES = "election_rules",
}

const documentTypeCount = Object.keys(DocumentType).length;
if (documentTypeCount > 5) {
  throw new Error(`DocumentType enum must contain at most 5 types, but found ${documentTypeCount}`);
}

export interface DocumentTypeInfo {
  type: DocumentType;
  title: string;
  description: string;
  comingSoon?: boolean;
  accent?: {
    border: string;
    bg: string;
    text: string;
    icon: string;
  };
}

const governingDocumentAccent = {
  border: "border-t-violet-500",
  bg: "bg-violet-50",
  text: "text-violet-700",
  icon: "text-violet-600",
};

export const DocumentTypes: DocumentTypeInfo[] = [
  {
    type: DocumentType.DECLARATION_COVENANTS,
    title: "Declaration of Covenants (CC&Rs)",
    description: "The HOA's primary governing document defining property rights, restrictions, maintenance responsibilities, and common areas.",
    comingSoon: false,
    accent: governingDocumentAccent,
  },
  {
    type: DocumentType.BYLAWS,
    title: "Bylaws",
    description: "Governs board elections, meetings, officers, committees, and association operations.",
    comingSoon: false,
    accent: governingDocumentAccent,
  },
  {
    type: DocumentType.RULES_REGULATIONS,
    title: "Rules & Regulations",
    description: "Community rules covering topics such as parking, pets, amenities, noise, and property use.",
    comingSoon: false,
    accent: governingDocumentAccent,
  },
  {
    type: DocumentType.ARCHITECTURAL_GUIDELINES,
    title: "Architectural Guidelines",
    description: "Design standards and approval requirements for exterior modifications and improvements.",
    comingSoon: false,
    accent: governingDocumentAccent,
  },
  {
    type: DocumentType.ELECTION_RULES,
    title: "Election Rules",
    description: "Procedures governing board member elections, nominations, ballots, and voting.",
    comingSoon: false,
    accent: governingDocumentAccent,
  },
];

if (DocumentTypes.length > 5) {
  throw new Error(`DocumentTypes array must contain at most 5 types, but found ${DocumentTypes.length}`);
}

export const DocumentTypeLabels: Record<DocumentType, string> = {
  [DocumentType.DECLARATION_COVENANTS]: "Declaration of Covenants (CC&Rs)",
  [DocumentType.BYLAWS]: "Bylaws",
  [DocumentType.RULES_REGULATIONS]: "Rules & Regulations",
  [DocumentType.ARCHITECTURAL_GUIDELINES]: "Architectural Guidelines",
  [DocumentType.ELECTION_RULES]: "Election Rules",
};

export const isGoverningDocumentType = (doc: { type?: string | null }): doc is UserDocument =>
  typeof doc.type === "string" &&
  DocumentTypes.some((documentType) => documentType.type === doc.type);

export interface UserDocument {
  id: string;
  /** Excluded from the "Search governing documents" document-mode chat.
   *  Never affects the main statute-based chat, which reads no uploads. */
  excludedFromSearch?: boolean;
  filename: string;
  type: DocumentType;
  size: number;
  uploadDate: string;
  url?: string;
  status?: "uploaded" | "processing" | "error";
  processing_status?:
    | "pending"
    | "processing"
    | "completed"
    | "failed"
    | "not_extractable"
    | "ocr_completed"
    | "malware_scanning"
    // Held while the optimize_pdf arq job runs (see backend PROJECT.md's
    // PDF-compression plan) — every clean-verdict PDF passes through this,
    // not just oversized ones, since page count is validated regardless of size.
    | "optimizing"
    // Terminal optimize-failure statuses. The five "*_after_compression"/
    // "*_limit_exceeded"/"*_unsupported"/"*_failed" outcomes are deterministic
    // (same bytes would fail identically again — action: remove & re-upload);
    // the two "*_timeout"/"*_internal_error" outcomes are retryable (arq
    // already retries automatically with backoff before surfacing these).
    | "too_large_after_compression"
    | "page_limit_exceeded"
    | "image_limit_exceeded"
    | "compression_unsupported"
    | "compression_failed"
    | "compression_timeout"
    | "compression_internal_error";
  errorMessage?: string;
  document_id?: number; // API uses numeric IDs
  title?: string; // Custom title for the document
  description?: string; // Description of the document
  isPublic?: boolean; // Whether document is publicly accessible
  isNew?: boolean; // For "New" badge display (computed from uploadDate)
  // Set only when confirmed present in the FAISS index as of the most recent
  // rebuild — null/undefined means "not yet indexed" or "no longer indexed"
  // (e.g. excludedFromSearch), NOT necessarily "processing failed."
  // processing_status reaching completed/ocr_completed does NOT guarantee
  // this by itself. "Ready to search" must key off
  // `indexedAt != null && !excludedFromSearch`, never processing_status alone.
  indexedAt?: string | null;
  // Human-readable reason for a terminal optimize-failure status — shown as
  // the failure copy alongside the recovery action.
  lastError?: string | null;
  /** Set while this upload is the pending replacement of another document, which stays in
   *  use until this one is ready (then the server retires it and clears this). */
  replacesDocumentId?: string | null;
  replacementIndexAttempts?: number;
  replacementIndexError?: string | null;
  /** The client's per-attempt upload id — lets a client that lost the response check whether
   *  the upload was accepted before trying again. */
  uploadAttemptId?: string | null;
}

export interface DocumentUploadState {
  uploading: boolean;
  progress: number;
  error: string | null;
  success: boolean;
}

export interface DocumentUploadProgress {
  documentId: string;
  filename: string;
  progress: number;
  status: "uploading" | "success" | "error";
  error?: string;
}

// Fallback validation only — the documents page validates against the upload capability
// manifest (lib/uploadCapabilities.ts). No legacy .doc: the server rejects it.
export const ALLOWED_FILE_TYPES = ["application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "text/plain"];
export const ALLOWED_FILE_EXTENSIONS = [".pdf", ".docx", ".txt"];
// Must match backend DOCUMENT_MAX_UPLOAD_BYTES (app/core/config.py) — same name, VITE_
// prefix, same unit (bytes). Override via VITE_DOCUMENT_MAX_UPLOAD_BYTES at build time;
// falls back to 25MB if unset or invalid.
const configuredMaxUploadBytes = Number(import.meta.env.VITE_DOCUMENT_MAX_UPLOAD_BYTES);
export const MAX_FILE_SIZE = configuredMaxUploadBytes > 0 ? configuredMaxUploadBytes : 25 * 1024 * 1024;

// Must match backend DOCUMENT_RAW_UPLOAD_MAX_BYTES (app/core/config.py) — same
// name, VITE_ prefix, same unit (bytes). This is the raw-intake ceiling for
// PDFs specifically — oversized PDFs are server-side optimized down toward
// MAX_FILE_SIZE; non-PDF uploads are still capped at MAX_FILE_SIZE directly.
// Falls back to 75MB if unset or invalid.
const configuredRawPdfUploadBytes = Number(import.meta.env.VITE_DOCUMENT_RAW_UPLOAD_MAX_BYTES);
export const MAX_RAW_PDF_UPLOAD_SIZE = configuredRawPdfUploadBytes > 0 ? configuredRawPdfUploadBytes : 75 * 1024 * 1024;
